// Pure helpers for the guest page's phone sign-in and menu photos
// (tests/unit/v2-web-dine.test.cjs).

// "(555) 010-1234" or "+44 20 7946 0958" -> E.164, or "" if it is not a number.
// Ten digits are US numbers (Scerv restaurants are in the US for now).
export function toE164(value) {
	const text = String(value || "").trim();
	const digits = text.replace(/\D/g, "");
	if (text.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : "";
	if (digits.length === 10) return `+1${digits}`;
	if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
	return "";
}

export const cleanFirstName = (value) => String(value || "").replace(/\s+/g, " ").trim().slice(0, 60);

// What went wrong, in words a guest can act on.
export function phoneErrorMessage(error) {
	switch (error?.code) {
		case "auth/invalid-phone-number":
			return "That phone number doesn't look right. Check it and try again.";
		case "auth/too-many-requests":
			return "Too many tries. Wait a few minutes, or use email instead.";
		case "auth/invalid-verification-code":
			return "That code is not right. Check the text and try again.";
		case "auth/code-expired":
			return "That code has expired. Go back and ask for a new one.";
		case "auth/captcha-check-failed":
		case "auth/missing-client-identifier":
		case "auth/unauthorized-domain":
		case "auth/operation-not-allowed":
			return "Texting isn't working right now. Use email instead.";
		case "auth/network-request-failed":
			return "No internet connection. Check your signal and try again.";
		default:
			return "We couldn't do that. Try again, or use email instead.";
	}
}

// The item's photo, the same field the app reads (imageUri, else imageUrl).
export const itemPhoto = (item) => {
	const url = String(item?.imageUri || item?.imageUrl || item?.image || "").trim();
	return /^https:\/\//.test(url) ? url : "";
};

// What is left to pay on the shared check. `lines` are the sent lines and
// `open` the ones with unit ranges nobody has paid or is paying
// (tableLogic.openLines). Once everything sent is paid the page says so and
// keeps ordering open instead of offering to pay again.
export function checkView(lines, open) {
	const openIds = new Set((open || []).map((line) => line.id));
	const owedCents = (open || []).reduce((sum, line) => sum + Number(line.openCents || 0), 0);
	return {
		owedCents,
		isOpenLine: (id) => openIds.has(id),
		allPaid: (lines || []).length > 0 && owedCents === 0,
	};
}

// Where a guest gets the app after paying (the same listings the V1 page uses).
export const APP_LINKS = {
	ios: "https://apps.apple.com/app/id1591335061",
	android: "https://play.google.com/store/apps/details?id=com.scerv.eat",
};

// The table's name as a guest reads it: "4" -> "Table 4", "Bar side 7" as
// it is, nothing -> "Your table" (Karl, 2026-11-03: the page must say which
// table you scanned).
export function tableLabel(name) {
	const text = String(name ?? "").replace(/\s+/g, " ").trim().slice(0, 40);
	if (!text) return "Your table";
	return /^[0-9]+[A-Za-z]?$/.test(text) ? `Table ${text}` : text;
}

// A guest may leave the table from the web page while nothing of theirs was
// sent to the kitchen and they have no payment going (Karl, 2026-11-03).
// Leaving drops their unsent items; anything sent keeps them at the table
// until it is paid. `state` is the engine table state, `me` the guest's id.
export function canLeaveTable(state, me) {
	if (!state || !me || state.tab || state.pickup) return false;
	if (state.status !== "open") return false;
	const member = state.members?.[me];
	if (!member || member.leftAt) return false;
	const lines = Object.values(state.lines || {});
	const mine = (line) => (line?.addedBy?.kind === "guest" && line.addedBy.id === me) || line?.forMemberId === me;
	if (lines.some((line) => line && line.status !== "draft" && mine(line))) return false;
	const payer = `guest:${me}`;
	return !Object.values(state.attempts || {}).some((attempt) => attempt?.payerKey === payer);
}
